import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsEnabledFlightsLuxPlusPricingInCurrentRegion, getIsEnabledLuxPlusCruiseInclusionsInCurrentRegion } from 'luxPlus/selectors/featureToggle'
import config from 'constants/config'
import { ASIAN_COUNTRIES } from 'constants/config/region'

export interface LuxPlusLandingAvailableProduct {
  type: 'hotels' | 'flights' | 'tours' | 'toursAndCruises' | 'cruises' | 'experiences'
  label: string
  lowercaseLabel: string
  to: string
}

interface LuxPlusLandingAvailableProducts {
  productListText: string
  products: Array<LuxPlusLandingAvailableProduct>
}

const EXPERIENCE_AVAILABLE_ASIAN_REGIONS = new Set(['SG', 'ID', 'TH', 'VN', 'AE'])

function useLuxPlusLandingAvailableProductList(): LuxPlusLandingAvailableProducts {
  const intl = useIntl()
  const isFlightsLuxPlusPricingEnabled = useAppSelector(getIsEnabledFlightsLuxPlusPricingInCurrentRegion)
  const isLuxPlusToursEnabled = useAppSelector(getIsLuxPlusToursEnabled)
  const isEnabledLuxPlusCruiseInclusions = useAppSelector(getIsEnabledLuxPlusCruiseInclusionsInCurrentRegion)
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const isStandaloneCruiseRegion = config.CRUISES_REGIONS.split(',').includes(currentRegionCode)
  const isExperiencesEnabled = config.EXPERIENCES_STANDLONE_ENABLED && (
    !ASIAN_COUNTRIES.includes(currentRegionCode) ||
    EXPERIENCE_AVAILABLE_ASIAN_REGIONS.has(currentRegionCode)
  )

  const hotelsLabel = intl.formatMessage({
    id: 'YuTKjGKLntsFSuLuLQTb',
    defaultMessage: 'hotels',
    description: 'Lowercase hotels label in LuxPlus landing page product list copy',
  })
  const flightsLabel = intl.formatMessage({
    id: 'Sd6M8W31BNqoRKFxKQ4S',
    defaultMessage: 'flights',
    description: 'Lowercase flights label in LuxPlus landing page product list copy',
  })
  const toursLabel = intl.formatMessage({
    id: 'qb4GLW4HFh5fUb5KAv8i',
    defaultMessage: 'tours',
    description: 'Lowercase tours label in LuxPlus landing page product list copy',
  })
  const toursAndCruisesLabel = intl.formatMessage({
    id: 'I-57Oyo42APydd8cfbPo',
    defaultMessage: 'tours & cruises',
    description: 'Lowercase combined tours and cruises label in LuxPlus landing page product list copy',
  })
  const cruisesLabel = intl.formatMessage({
    id: 'BvkayzD3wm1Z_5Stf3ku',
    defaultMessage: 'cruises',
    description: 'Lowercase cruises label in LuxPlus landing page product list copy',
  })
  const experiencesLabel = intl.formatMessage({
    id: 'n3JcuDSJhNvK0F4zBtCB',
    defaultMessage: 'experiences',
    description: 'Lowercase experiences label in LuxPlus landing page product list copy',
  })
  const hotelsPillLabel = intl.formatMessage({
    id: 'eYXwZnSymCZ2WS1v-bsh',
    defaultMessage: 'Hotels',
    description: 'Clickable pill label for hotels on the LuxPlus landing page',
  })
  const flightsPillLabel = intl.formatMessage({
    id: '_52pqaf_jsv2lz-R1LWl',
    defaultMessage: 'Flights',
    description: 'Clickable pill label for flights on the LuxPlus landing page',
  })
  const toursPillLabel = intl.formatMessage({
    id: '5FidPJSnDEeqIzOV6FC4',
    defaultMessage: 'Tours',
    description: 'Clickable pill label for tours on the LuxPlus landing page',
  })
  const toursAndCruisesPillLabel = intl.formatMessage({
    id: 'mXZij_A33UFoSuGK0SmF',
    defaultMessage: 'Tours & Cruises',
    description: 'Clickable combined pill label for tours and cruises on the LuxPlus landing page',
  })
  const cruisesPillLabel = intl.formatMessage({
    id: '0MLRcMhPbO7a9INT_qli',
    defaultMessage: 'Cruises',
    description: 'Clickable pill label for cruises on the LuxPlus landing page',
  })
  const experiencesPillLabel = intl.formatMessage({
    id: 'ABICpM5jtf3rOLPPPyXG',
    defaultMessage: 'Experiences',
    description: 'Clickable pill label for experiences on the LuxPlus landing page',
  })

  return useMemo(() => {
    const products: Array<LuxPlusLandingAvailableProduct> = [
      { type: 'hotels', label: hotelsPillLabel, lowercaseLabel: hotelsLabel, to: '/hotels' },
    ]

    if (isFlightsLuxPlusPricingEnabled) {
      products.push({ type: 'flights', label: flightsPillLabel, lowercaseLabel: flightsLabel, to: '/flights' })
    }
    if (isLuxPlusToursEnabled && isStandaloneCruiseRegion) {
      products.push({ type: 'tours', label: toursPillLabel, lowercaseLabel: toursLabel, to: '/tours' })
    }
    if (isLuxPlusToursEnabled && !isStandaloneCruiseRegion) {
      products.push({ type: 'toursAndCruises', label: toursAndCruisesPillLabel, lowercaseLabel: toursAndCruisesLabel, to: '/tours' })
    }
    if (isEnabledLuxPlusCruiseInclusions && isStandaloneCruiseRegion) {
      products.push({ type: 'cruises', label: cruisesPillLabel, lowercaseLabel: cruisesLabel, to: '/cruises' })
    }
    if (isExperiencesEnabled) {
      products.push({ type: 'experiences', label: experiencesPillLabel, lowercaseLabel: experiencesLabel, to: '/experiences' })
    }

    return {
      productListText: intl.formatList(products.map(product => product.lowercaseLabel), { type: 'conjunction' }),
      products,
    }
  }, [
    intl,
    hotelsLabel,
    flightsLabel,
    toursLabel,
    toursAndCruisesLabel,
    cruisesLabel,
    experiencesLabel,
    hotelsPillLabel,
    flightsPillLabel,
    toursPillLabel,
    toursAndCruisesPillLabel,
    cruisesPillLabel,
    experiencesPillLabel,
    isFlightsLuxPlusPricingEnabled,
    isLuxPlusToursEnabled,
    isStandaloneCruiseRegion,
    isEnabledLuxPlusCruiseInclusions,
    isExperiencesEnabled,
  ])
}

export default useLuxPlusLandingAvailableProductList
